import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Download, Image as ImageIcon, Facebook, Linkedin, Twitter, FileText, Palette } from 'lucide-react';

const MediaKit = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Admin check
  React.useEffect(() => {
    if (user && !user.is_admin) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Set noindex for SEO
  React.useEffect(() => {
    const metaTag = document.createElement('meta');
    metaTag.name = 'robots';
    metaTag.content = 'noindex, nofollow';
    document.head.appendChild(metaTag);
    
    return () => {
      document.head.removeChild(metaTag);
    };
  }, []);

  if (!user?.is_admin) {
    return null;
  }

  const downloadAsset = (path, filename) => {
    const link = document.createElement('a');
    link.href = path;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const assets = {
    screenshots: [
      { name: 'AI Advisors (4 Specialists)', file: '/screenshots/advisors.png', desc: 'Choose Your AI Tax Advisor page', platforms: 'All' },
      { name: 'Dashboard (Tax Health)', file: '/screenshots/dashboard.png', desc: 'Real-time tax estimates', platforms: 'All' },
      { name: 'Transactions (General Ledger)', file: '/screenshots/transactions.png', desc: 'AI categorization in action', platforms: 'All' },
      { name: 'Bank Connections', file: '/screenshots/bank.png', desc: 'Stripe Financial Connections', platforms: 'All' },
      { name: 'Mileage Tracker', file: '/screenshots/mileage.png', desc: 'Trip logging and deductions', platforms: 'All' },
      { name: 'Reports (CPA-Grade PDF)', file: '/screenshots/reports.png', desc: 'Professional tax packages', platforms: 'All' },
      { name: 'Receipt Shoebox', file: '/screenshots/receipts.png', desc: 'OCR and organization', platforms: 'All' },
      { name: 'CSV Import', file: '/screenshots/csv_import.png', desc: 'Intelligent field mapping', platforms: 'All' }
    ],
    copy: {
      facebook: [
        { type: 'Founding Offer', text: "🚀 First 100 Get $79/Year Forever!\n\nTax software built for 1099 contractors:\n✅ 4 AI Tax Advisors\n✅ Auto bank sync\n✅ 95% AI categorization\n✅ Real-time estimates\n\nKeeper Tax: $199/year\nSolelyTax: $79/year (founding members)\n\n💰 Save $120/year!\n\nOnly 97 spots left! 👇\napp.solelytax.ai/waitlist" },
        { type: '4 AI Advisors', text: "Meet your AI tax team 🤖\n\n👩‍💼 Carlotta (AI Tax Advisor)\n👨‍💼 Justin Wu (Financial Analyst)\n👩‍💼 Sabrina Chen (Bookkeeper)\n👨‍⚖️ David Martinez (Business Attorney)\n\n4 AI specialists vs competitors' 1 general bot.\n\nGet expert guidance 24/7 for just $79/year.\n\napp.solelytax.ai/how-it-works" }
      ],
      linkedin: [
        { type: 'Founding Announcement', text: "Excited to announce SolelyTax AI's Founding Member Program 🚀\n\nWe've built AI-powered tax management specifically for 1099 contractors and sole entrepreneurs.\n\nKey Features:\n• Stripe-powered automatic bank sync\n• 4 specialized AI tax advisors (CPA, Financial Analyst, Bookkeeper, Attorney)\n• 95% AI categorization accuracy\n• Real-time federal, state, and SE tax estimates\n• CPA-grade professional reports\n• Full Spanish language support\n\nFounder Pricing: $79/year (first 100 members)\nRegular: $228/year\n\nWe're looking for 97 more founding members to join us. Interested in simplified tax management?\n\nLearn more: app.solelytax.ai/how-it-works" },
        { type: 'Problem/Solution', text: "The average 1099 contractor leaves $5,200 in deductions unclaimed every year.\n\nWhy? Manual categorization is tedious. Generic tax software doesn't understand freelance work.\n\nSolelyTax AI solves this:\n✓ AI categorizes 95% of transactions automatically\n✓ Finds every deduction (home office, mileage, supplies)\n✓ Real-time tax estimates (no surprises)\n✓ CPA-ready reports\n\nBuilt specifically for the self-employed.\n\nFounder pricing: $79/year for first 100.\n\napp.solelytax.ai/waitlist" }
      ],
      twitter: [
        { type: 'Founder Offer', text: "Tax software for $79/year (vs Keeper's $199)\n\n✅ 4 AI advisors\n✅ Bank sync\n✅ Real-time estimates\n✅ CPA reports\n✅ Spanish support\n\nFirst 100 get founder pricing.\n97 spots left.\n\napp.solelytax.ai/waitlist" },
        { type: 'Feature Thread (Tweet 1/4)', text: "Built tax software for 1099 contractors.\n\nHere's how it works 🧵👇" },
        { type: 'Feature Thread (Tweet 2/4)', text: "1. Connect your bank (Stripe-secure)\n\nAuto-sync transactions. No manual entry.\n\n[attach: bank.png]" },
        { type: 'Feature Thread (Tweet 3/4)', text: "2. AI categorizes everything\n\n95% accuracy. 80+ smart rules.\n\nReview in 5 minutes, not 5 hours.\n\n[attach: transactions.png]" },
        { type: 'Feature Thread (Tweet 4/4)', text: "3. Export to your CPA\n\n8-section professional package.\nSchedule C ready.\n\nFirst 100: $79/year\n\napp.solelytax.ai/waitlist" }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50">
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-emerald-600 text-white px-4 py-2">
            <Palette className="w-4 h-4 mr-2" />
            Marketing Assets
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            SolelyTax AI Media Kit
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Marketing assets for founder promotion across Facebook, LinkedIn, and X.com
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid sm:grid-cols-3 gap-4 mb-12 max-w-3xl mx-auto">
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-emerald-600">8</p>
              <p className="text-sm text-gray-600">App Screenshots</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-blue-600">3</p>
              <p className="text-sm text-gray-600">Social Platforms</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-purple-600">15+</p>
              <p className="text-sm text-gray-600">Copy Templates</p>
            </CardContent>
          </Card>
        </div>

        {/* Screenshots Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="w-6 h-6" />
              Product Screenshots (All Platforms)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {assets.screenshots.map((asset, idx) => (
                <div key={idx} className="group">
                  <div className="relative overflow-hidden rounded-lg border-2 border-gray-200 hover:border-emerald-500 transition-all mb-2">
                    <img 
                      src={asset.file}
                      alt={asset.name}
                      className="w-full h-auto group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <p className="font-semibold text-sm text-gray-900">{asset.name}</p>
                  <p className="text-xs text-gray-600 mb-2">{asset.desc}</p>
                  <Button 
                    size="sm"
                    variant="outline"
                    onClick={() => downloadAsset(asset.file, asset.name + '.png')}
                    className="w-full"
                  >
                    <Download className="w-3 h-3 mr-1" />
                    Download
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Copy Templates - Facebook */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Facebook className="w-6 h-6 text-blue-600" />
              Facebook Copy Templates
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {assets.copy.facebook.map((template, idx) => (
              <div key={idx} className="bg-slate-50 rounded-lg p-4 border border-gray-200">
                <p className="font-semibold text-sm text-gray-700 mb-2">{template.type}</p>
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans">{template.text}</pre>
                <Button 
                  size="sm"
                  variant="outline"
                  className="mt-3"
                  onClick={() => {
                    navigator.clipboard.writeText(template.text);
                    alert('Copied to clipboard!');
                  }}
                >
                  Copy Text
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Copy Templates - LinkedIn */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Linkedin className="w-6 h-6 text-blue-700" />
              LinkedIn Copy Templates
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {assets.copy.linkedin.map((template, idx) => (
              <div key={idx} className="bg-slate-50 rounded-lg p-4 border border-gray-200">
                <p className="font-semibold text-sm text-gray-700 mb-2">{template.type}</p>
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans">{template.text}</pre>
                <Button 
                  size="sm"
                  variant="outline"
                  className="mt-3"
                  onClick={() => {
                    navigator.clipboard.writeText(template.text);
                    alert('Copied to clipboard!');
                  }}
                >
                  Copy Text
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Copy Templates - X.com */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Twitter className="w-6 h-6 text-gray-900" />
              X.com (Twitter) Copy Templates
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {assets.copy.twitter.map((template, idx) => (
              <div key={idx} className="bg-slate-50 rounded-lg p-4 border border-gray-200">
                <p className="font-semibold text-sm text-gray-700 mb-2">{template.type}</p>
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans">{template.text}</pre>
                <Button 
                  size="sm"
                  variant="outline"
                  className="mt-3"
                  onClick={() => {
                    navigator.clipboard.writeText(template.text);
                    alert('Copied to clipboard!');
                  }}
                >
                  Copy Text
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Usage Guidelines */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-6 h-6" />
              Usage Guidelines & Best Practices
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none">
            <h3>Platform-Specific Recommendations:</h3>
            
            <h4>Facebook:</h4>
            <ul>
              <li><strong>Best time to post:</strong> 9am-11am, 1pm-3pm (user's local time)</li>
              <li><strong>Frequency:</strong> 1-2 posts/day during founder campaign</li>
              <li><strong>Use:</strong> Founding offer + 4 AI Advisors screenshot</li>
              <li><strong>Hashtags:</strong> #TaxSoftware #Freelance #1099Contractor #SmallBusiness</li>
            </ul>

            <h4>LinkedIn:</h4>
            <ul>
              <li><strong>Best time:</strong> Tuesday-Thursday, 8am-10am, 12pm, 5pm-6pm</li>
              <li><strong>Frequency:</strong> 3-4 posts/week</li>
              <li><strong>Use:</strong> Professional tone, feature highlights, problem/solution</li>
              <li><strong>Hashtags:</strong> #TaxPlanning #SelfEmployed #Entrepreneurship #FinTech</li>
            </ul>

            <h4>X.com (Twitter):</h4>
            <ul>
              <li><strong>Best time:</strong> 9am, 12pm, 3pm, 6pm (high engagement windows)</li>
              <li><strong>Frequency:</strong> 3-5 tweets/day (threads work well)</li>
              <li><strong>Use:</strong> Short, punchy, thread format for features</li>
              <li><strong>Hashtags:</strong> #Tax #SoloPreneur #Freelance #1099</li>
            </ul>

            <h3>UTM Tracking:</h3>
            <ul>
              <li><strong>Facebook:</strong> ?utm_source=facebook&utm_campaign=founding_launch</li>
              <li><strong>LinkedIn:</strong> ?utm_source=linkedin&utm_campaign=founding_launch</li>
              <li><strong>X.com:</strong> ?utm_source=twitter&utm_campaign=founding_launch</li>
            </ul>

            <h3>Key Messages:</h3>
            <ul>
              <li>✅ First 100 get $79/year forever (saves $149/year)</li>
              <li>✅ 4 AI advisors vs competitors' 1</li>
              <li>✅ 60% cheaper than Keeper Tax ($199)</li>
              <li>✅ Built specifically for 1099 contractors</li>
              <li>✅ Spanish support (unique in market)</li>
            </ul>
          </CardContent>
        </Card>

        {/* Quick Links */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Links for Social Posts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                <span className="font-mono text-sm">app.solelytax.ai/waitlist</span>
                <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText('https://app.solelytax.ai/waitlist')}>
                  Copy
                </Button>
              </div>
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <span className="font-mono text-sm">app.solelytax.ai/how-it-works</span>
                <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText('https://app.solelytax.ai/how-it-works')}>
                  Copy
                </Button>
              </div>
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                <span className="font-mono text-sm">app.solelytax.ai/waitlist/founding</span>
                <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText('https://app.solelytax.ai/waitlist/founding')}>
                  Copy
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Back Button */}
        <div className="mt-12 text-center">
          <Button variant="outline" onClick={() => navigate('/')}>
            ← Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MediaKit;
